import { BetaSchemaForm } from "@ant-design/pro-components";
import { Spin } from "antd";
import { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useUpdate, useInvalidate } from "@refinedev/core";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { useResourceContext } from "@/layouts/ResourceContext";
import { useTheme } from "antd-style";
import { requiredRule } from "@/hooks/config/useBasicComponent";
import { validateInstanceDomain } from "@/apis";
import { DomainNameInput } from "../../config/DomainNameInput";
import { debounce } from "lodash";
import { UpdateWrapper } from "@/layouts/UpdateWrapper";

export const UpdateDomainName = ({ id, action, goto }) => {
  const { state } = useResourceContext();

  const { t } = useTranslation();
  const [validateLoading, setValidateLoading] = useState(false);
  const token = useTheme();
  const oldName = state.selectedResources[0].subdomain;

  const debouncedValidate = useCallback(
    debounce(async (form, dataIndex) => {
      await form.validateFields([dataIndex]);
    }, 1000),
    [],
  );

  const columns = [
    {
      title: t("config.instance.form.oldDomainName", "Old Domain Name"),
      dataIndex: "oldDomainName",
      initialValue: oldName,
      width: "400px",
      fieldProps: {
        // readOnly: true,
        // style: {
        //   pointerEvents: "none",
        //   backgroundColor: token.controlItemBgActiveDisabled,
        // },
        disabled: true,
      },
    },
    {
      title: t("config.instance.form.newDomainName", "New Domain Name"),
      dataIndex: "newDomainName",
      width: "m",
      renderFormItem: (item, _, form) => {
        return (
          <DomainNameInput
            mode="update"
            oldValue={oldName}
            loading={validateLoading}
            value=""
            onChange={async (newVariables) => {
              form.setFieldsValue({
                [item.dataIndex]: newVariables,
              });
              // await form.validateFields([item.dataIndex]);
              debouncedValidate(form, item.dataIndex);
            }}
            onCheckDuplicated={async () => {
              setValidateLoading(true);
              try {
                await form.validateFields([item.dataIndex]);
              } finally {
                setValidateLoading(false);
              }
              // form.setFields([
              //   {
              //     name: item.dataIndex,
              //     // errors: ["This domain name is already taken."],
              //     errors: [],
              //   },
              // ]);
            }}
          />
        );
      },
      formItemProps: {
        rules: [
          ...requiredRule(t),
          {
            async validator(_, value) {
              // this is checked by required rule
              if (value === undefined || value === "") return Promise.resolve();

              if (value === oldName) {
                return Promise.reject(
                  new Error(
                    t(
                      "config.instance.error.domainNameSame",
                      "Please provide a different domain name for the update.",
                    ),
                  ),
                );
              }
              const data = await validateInstanceDomain(id, value);
              if (data.status === "OK") {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  t("config.instance.error.domainName", { domain: value }),
                ),
              );
            },
          },
        ],
      },
    },
  ];

  const formRef = useRef();
  const [submitting, setSubmitting] = useState(false);

  const navigate = useResourceNavigate();
  const invalidate = useInvalidate();

  const { mutate: update } = useUpdate({
    resource: "instances",
    successNotification: (data, values, resource) => {
      setSubmitting(false);
      invalidate({
        resource,
        invalidates: ["list"],
      });
      navigate({
        resource: "instances",
        key: id,
        action: "show",
      });
      return {
        message: t(`instances.actionResult.${action}.success`, {
          id,
        }),
        type: "success",
      };
    },
    errorNotification: (data, values, resource) => {
      setSubmitting(false);
      return {
        message: t(`instances.actionResult.${action}.failed`, {
          id,
        }),
        type: "error",
      };
    },
  });

  return (
    <Spin spinning={submitting}>
      <UpdateWrapper
        i18nTitle={`actions.${action}`}
        title={t(`actions.${action}`)}
        desc={t(`actionsDesc.${action}`)}
        footer={<></>}
      >
        <BetaSchemaForm
          formRef={formRef}
          onFinish={async (values) => {
            setSubmitting(true);
            update({
              id,
              values: { action, data: { domain: values.newDomainName } },
            });
          }}
          columns={columns}
        />
      </UpdateWrapper>
    </Spin>
  );
};
